import { t } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Skeleton } from '@/components/ui/skeleton';
import { TableTitle } from '@/components/ui/table-title';
import { piecesHooks } from '@/features/pieces/lib/pieces-hook';
import { cn } from '@/lib/utils';
import { PieceMetadataModelSummary } from '@activepieces/pieces-framework';

import {
  CatalogCategory,
  parseCatalogCategory,
} from '../lib/catalog-categories';
import { catalogCategoryLabel } from '../lib/catalog-labels';
import {
  CATEGORY_QUERY_PARAM,
  catalogLinkState,
  SEARCH_QUERY_PARAM,
} from '../lib/catalog-navigation';
import { buildCatalogView, CatalogView } from '../lib/catalog-view';

import { PieceCatalogCard } from './piece-catalog-card';

const SKELETON_CARD_COUNT = 12;
const CARD_GRID_CLASS =
  'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

type PieceCatalogProps = {
  detailPath: (pieceName: string) => string;
};

type CategoryEntryProps = {
  label: string;
  count: number;
  selected: boolean;
  onSelect: () => void;
};

const CategoryEntry = ({
  label,
  count,
  selected,
  onSelect,
}: CategoryEntryProps) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(
      'flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm transition-colors hover:bg-accent',
      selected && 'bg-accent font-medium text-primary',
    )}
  >
    <span className="truncate">{label}</span>
    <span className="text-xs text-muted-foreground">{count}</span>
  </button>
);

type CategoryNavProps = {
  view: CatalogView<PieceMetadataModelSummary>;
  category: CatalogCategory | null;
  onSelectCategory: (category: CatalogCategory | null) => void;
};

const CategoryNav = ({
  view,
  category,
  onSelectCategory,
}: CategoryNavProps) => (
  <nav className="flex flex-col gap-0.5 md:sticky md:top-4 md:self-start">
    <CategoryEntry
      label={t('All')}
      count={view.matchingCount}
      selected={category === null}
      onSelect={() => onSelectCategory(null)}
    />
    {view.categoryCounts.map((entry) => (
      <CategoryEntry
        key={entry.category}
        label={catalogCategoryLabel(entry.category)}
        count={entry.count}
        selected={entry.category === category}
        onSelect={() => onSelectCategory(entry.category)}
      />
    ))}
  </nav>
);

type CatalogGridProps = {
  pieces: PieceMetadataModelSummary[];
  onClearFilters: () => void;
  detailPath: (pieceName: string) => string;
};

const CatalogGrid = ({
  pieces,
  onClearFilters,
  detailPath,
}: CatalogGridProps) => {
  const location = useLocation();
  const linkState = catalogLinkState(location.search);

  if (pieces.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
        <p>{t('No app matches these filters')}</p>
        <Button variant="outline" onClick={onClearFilters}>
          {t('Clear filters')}
        </Button>
      </div>
    );
  }
  return (
    <div className={CARD_GRID_CLASS}>
      {pieces.map((piece) => (
        <PieceCatalogCard
          key={piece.name}
          piece={piece}
          to={detailPath(piece.name)}
          linkState={linkState}
        />
      ))}
    </div>
  );
};

export const PieceCatalog = ({ detailPath }: PieceCatalogProps) => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get(SEARCH_QUERY_PARAM) ?? '';
  const category = parseCatalogCategory(searchParams.get(CATEGORY_QUERY_PARAM));
  const { pieces, isLoading, isError } = piecesHooks.usePieces({});
  const isReady = !isLoading && !isError;

  const view = useMemo(
    () =>
      buildCatalogView({
        pieces: pieces ?? [],
        search,
        category,
        locale: i18n.language,
      }),
    [pieces, search, category, i18n.language],
  );

  const updateQueryParam = (key: string, value: string | null) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  };

  const renderResults = () => {
    if (isError) {
      return (
        <p className="text-destructive">{t('Failed to load the catalog')}</p>
      );
    }
    if (isLoading) {
      return (
        <div className={CARD_GRID_CLASS}>
          {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
            <Skeleton key={index} className="h-32 rounded-lg" />
          ))}
        </div>
      );
    }
    return (
      <CatalogGrid
        pieces={view.pieces}
        onClearFilters={() =>
          setSearchParams(new URLSearchParams(), { replace: true })
        }
        detailPath={detailPath}
      />
    );
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <TableTitle
        description={t(
          'Every app a Kizeo client can use in a flow, with its triggers and actions',
        )}
      >
        {t('Catalog')}
      </TableTitle>
      <div className="grid gap-6 md:grid-cols-[13rem_1fr]">
        {isReady ? (
          <CategoryNav
            view={view}
            category={category}
            onSelectCategory={(selected) =>
              updateQueryParam(CATEGORY_QUERY_PARAM, selected)
            }
          />
        ) : (
          <div />
        )}
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex h-10 w-full max-w-md items-center rounded-md border bg-background shadow-sm">
            <SearchInput
              autoFocus
              value={search}
              placeholder={t('Search an app')}
              showDeselect={search !== ''}
              onChange={(value) => updateQueryParam(SEARCH_QUERY_PARAM, value)}
            />
          </div>
          {renderResults()}
        </div>
      </div>
    </div>
  );
};
